export const APP_ROLES = [
  'admin',
  'executive',
  'finance_credit_approver',
  'sales_account_manager',
  'technical_delivery',
  'read_only_auditor',
] as const;

export type AppRole = (typeof APP_ROLES)[number];

export const CAPABILITIES = [
  'profitability.read',
  'profitability.write',
  'records.read',
  'records.write',
  'audit.read',
  'credit.approve',
  'exchange_rate.approve',
  'integration.xero.sync',
  'configuration.manage',
  'sensitive.export',
] as const;

export type Capability = (typeof CAPABILITIES)[number];

const ROLE_CAPABILITIES: Readonly<Record<AppRole, readonly Capability[]>> = {
  admin: CAPABILITIES,
  executive: [
    'records.read',
    'records.write',
    'audit.read',
    'profitability.read',
    'profitability.write',
    'credit.approve',
    'exchange_rate.approve',
    'integration.xero.sync',
    'sensitive.export',
  ],
  finance_credit_approver: [
    'records.read',
    'records.write',
    'audit.read',
    'profitability.read',
    'profitability.write',
    'credit.approve',
    'exchange_rate.approve',
  ],
  sales_account_manager: ['records.read', 'records.write', 'audit.read'],
  technical_delivery: ['records.read', 'records.write', 'audit.read'],
  read_only_auditor: ['records.read', 'audit.read'],
};

export function hasCapability(roles: readonly AppRole[], capability: Capability): boolean {
  return roles.some((role) => ROLE_CAPABILITIES[role].includes(capability));
}

export interface EntraClaims {
  oid: string;
  tid: string;
  roles?: readonly string[];
  groups?: readonly string[];
  hasGroupsOverage?: boolean;
}

export interface EntraRoleMapping {
  tenantId: string;
  appRoleValues: Readonly<Partial<Record<AppRole, string>>>;
  groupObjectIds: Readonly<Partial<Record<AppRole, string>>>;
}

export interface RoleResolution {
  roles: AppRole[];
  requiresGraphLookup: boolean;
}

export function resolveEntraRoles(claims: EntraClaims, mapping: EntraRoleMapping): RoleResolution {
  if (claims.tid !== mapping.tenantId) return { roles: [], requiresGraphLookup: false };

  const claimRoles = new Set(claims.roles ?? []);
  const claimGroups = new Set(claims.groups ?? []);
  const roles = APP_ROLES.filter((role) => {
    const appRole = mapping.appRoleValues[role];
    const groupId = mapping.groupObjectIds[role];
    return (
      (appRole !== undefined && claimRoles.has(appRole)) ||
      (groupId !== undefined && claimGroups.has(groupId))
    );
  });

  return { roles, requiresGraphLookup: claims.hasGroupsOverage === true };
}
